import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Alert
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LearningProgressModal from './LearningProgressModal';
// PageApprovalModal removed - no longer needed for word-count-based streaming
import DocumentPlanModal from './DocumentPlanModal';
import { filterChildrenForContentGeneration } from '../utils/contentGenerationSubtasks';
import { outcomeModuleDisplayName } from '../utils/outcomeModuleName';
import { estimatedPdfPages, resolveTaskCredits, wordRangeFromCredits } from '../utils/learningContentSizing';

const VECTOR_STORE_ID = 'vs_6926dd5afba08191bec66ce4fc56d08a';
const VECTOR_STORE_GUIDELINE = `Follow the writing guideline stored in vector store ${VECTOR_STORE_ID}: maintain an academic yet accessible tone, cite every claim with Harvard references, prioritise South African sources, avoid Wikipedia, and keep the HTML ready for PDF layout with valid visuals.`;

// Optional debug telemetry: set REACT_APP_DEBUG_INGEST_URL to enable (e.g. http://127.0.0.1:7243/ingest/...)
const DEBUG_INGEST_URL = process.env.REACT_APP_DEBUG_INGEST_URL || '';

export default function TaskAgentBuilder({ 
  task, 
  module, 
  qualification,
  onComplete,
  onSaved,
  onCleared,
  showViewSavedButton = false,
  onViewSavedContent
}) {
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [runId, setRunId] = useState(null);
  const [debugLogs, setDebugLogs] = useState([]);
  const [status, setStatus] = useState(null);
  const [responseHtml, setResponseHtml] = useState('');
  const [responseJson, setResponseJson] = useState(null);
  const streamTextRef = useRef(''); // Use ref for large HTML string to reduce memory
  const [streamText, setStreamText] = useState(''); // Accumulated stream text deltas (for reactivity)
  const responseMetaRef = useRef({ pageCount: 0, totalWords: 0, avgWordsPerPage: 0, totalLength: 0, run_id: null }); // Use ref to break dependency chain
  const wordCountReachedAtDoneRef = useRef(false); // Authoritative: set in 'done' event, used when closing to avoid false "word count not reached"
  const totalWordsAtDoneRef = useRef(0);
  const targetWordRangeRef = useRef({ min: 9000, max: 11000 }); // Current run's word range (used in build payload; state targetWordRange is for UI)
  const taskCredits = resolveTaskCredits(task, module);
  const creditBasedRange = wordRangeFromCredits(taskCredits);
  const creditBasedPages = estimatedPdfPages(creditBasedRange);
  const regenerateFromScratchRef = useRef(false); // When true, dialog confirm should run clear + startBuild (Regenerate from scratch)
  const [progressSnapshots, setProgressSnapshots] = useState([]); // Store progress snapshots for info popup
  
  // Helper function to sanitize debug log details (remove large objects)
  const sanitizeDebugDetails = (details) => {
    if (!details || typeof details !== 'object') return details;
    const sanitized = {};
    // Only keep essential fields, exclude large objects
    const allowedFields = ['error', 'message', 'level', 'totalWords', 'streamTextLength', 'page_number', 'title', 'word_count', 'totalPages', 'eventNumber', 'isInitialLoad', 'liveWordCount', 'deltaWordCount', 'hasContent'];
    for (const key of allowedFields) {
      if (details[key] !== undefined) {
        sanitized[key] = details[key];
      }
    }
    // For error objects, keep message and stack but limit stack length
    if (details.error) {
      sanitized.error = typeof details.error === 'string' ? details.error : details.error?.message || 'Error';
    }
    if (details.stack) {
      sanitized.stack = typeof details.stack === 'string' ? details.stack.substring(0, 200) : details.stack;
    }
    return sanitized;
  };
  
  // Helper function to filter out internal tool call data from streamed content
  // This removes JSON query objects, URL fragments, and tool parameters that are
  // internal agent operations and should NOT be displayed to the user
  const filterToolCallData = (content) => {
    if (!content || typeof content !== 'string') return content;
    
    let filtered = content;
    
    // ============= CRITICAL: Handle incremental streaming patterns =============
    // The LLM streams tool call arguments character-by-character, creating patterns like:
    // {"query":"{"query":"Har{"query":"Harvard{"query":"Harvard ref...
    // {"url":"https{"url":"https://{"url":"https://scholar...
    // These accumulate incrementally and need aggressive removal
    
    // STEP 1: Remove entire blocks of incremental JSON tool call building
    // Match from first {"query": or {"url": to the end of that sequence (before HTML or end)
    // This regex finds sequences of {"query": or {"url": that repeat/build incrementally
    // and removes them entirely
    
    // Pattern A1: Remove all text from {"query" (with or without colon) until we hit an HTML tag or end
    // This catches: {"query":"{"query":"Har{"query":"Harvard... all the way to the last one
    // Also catches incomplete: {"query" (without colon)
    filtered = filtered.replace(/\{"query"[^<]*(?=<|$)/g, '');
    
    // Pattern A2: Remove all text from {"url" (with or without colon) until we hit an HTML tag or end
    filtered = filtered.replace(/\{"url"[^<]*(?=<|$)/g, '');
    
    // Pattern A3: Remove standalone "query" or "url" strings that remain after removing the object
    // These are orphaned property names left behind
    filtered = filtered.replace(/^"query"/g, '');
    filtered = filtered.replace(/^"url"/g, '');
    filtered = filtered.replace(/\s+"query"/g, '');
    filtered = filtered.replace(/\s+"url"/g, '');
    
    // STEP 2: Handle cases where JSON is mixed with partial HTML tags
    // Sometimes the pattern is: text{"query":...text (without clean HTML boundary)
    // Look for {"query": or {"url": and remove everything up to the next proper content
    
    // Pattern B1: Incremental query building pattern (the full sequence)
    // Matches: {"query":"{"query":"word{"query":"word word...
    // The key insight: each repetition contains the previous content plus more
    let prevFiltered = '';
    let safetyCounter = 0;
    while (prevFiltered !== filtered && safetyCounter < 10) {
      prevFiltered = filtered;
      safetyCounter++;
      // Remove any {"query": followed by content that contains another {"query":
      filtered = filtered.replace(/\{"query":"[^"]*\{"query":/g, '{"query":');
      filtered = filtered.replace(/\{"url":"[^"]*\{"url":/g, '{"url":');
    }
    
    // STEP 3: Remove complete JSON tool call objects
    // These may appear as properly formed JSON after the incremental patterns
    
    // Pattern C1: Query objects with various metadata
    filtered = filtered.replace(/\{"query"\s*:\s*"[^"]*"\s*,\s*"numResults"\s*:\s*\d+[^}]*\}/g, '');
    filtered = filtered.replace(/\{"query"\s*:\s*"[^"]*"\s*,\s*"run_id"\s*:\s*"[^"]*"[^}]*\}/g, '');
    filtered = filtered.replace(/\{"query"\s*:\s*"[^"]*"\s*,\s*"topKChunks"\s*:\s*\d+[^}]*\}/g, '');
    filtered = filtered.replace(/\{"query"\s*:\s*"[^"]*"\}/g, '');
    
    // Pattern C2: URL objects
    filtered = filtered.replace(/\{"url"\s*:\s*"[^"]*"\}/g, '');
    filtered = filtered.replace(/\{"url"\s*:\s*[^}]*\}/g, '');
    
    // Pattern C3: Run ID objects
    filtered = filtered.replace(/\{"run_id"\s*:\s*"[^"]*"\s*,\s*"totalWordsSoFar"\s*:\s*\d+\}/g, '');
    filtered = filtered.replace(/\{"run_id"\s*:\s*"[^"]*"\}/g, '');
    
    // Pattern C4: Other metadata objects
    filtered = filtered.replace(/"progressStateSnapshot"\s*:\s*\{[^}]{0,2000}?\}/g, '');
    filtered = filtered.replace(/"progressStateSnapshot"\s*:\s*\{[^<]*/g, '');
    filtered = filtered.replace(/\{"docId"[^}]*\}/g, '');
    filtered = filtered.replace(/\{"targetWordRange"\s*:\s*\{[^}]*\}[^}]*\}/g, '');
    
    // Pattern C5: Any remaining JSON with query, url, or run_id
    filtered = filtered.replace(/\{[^}]*"query"[^}]*\}/g, '');
    filtered = filtered.replace(/\{[^}]*"url"[^}]*\}/g, '');
    filtered = filtered.replace(/\{[^}]*"run_id"[^}]*\}/g, '');
    
    // STEP 4: Remove incomplete JSON at end or in middle
    // Pattern D1: Incomplete query objects anywhere in the string (with or without colon)
    filtered = filtered.replace(/\{"query"[^}]*/g, '');
    filtered = filtered.replace(/\{"query"/g, '');
    
    // Pattern D2: Incomplete URL objects anywhere (with or without colon)
    filtered = filtered.replace(/\{"url"[^}]*/g, '');
    filtered = filtered.replace(/\{"url"/g, '');
    
    // Pattern D3: Remove any remaining orphaned "query" or "url" strings
    filtered = filtered.replace(/"query"/g, '');
    filtered = filtered.replace(/"url"/g, '');
    
    // STEP 5: Remove orphaned JSON metadata
    filtered = filtered.replace(/"numResults"\s*:\s*\d+\s*,?/g, '');
    filtered = filtered.replace(/"topKChunks"\s*:\s*\d+\s*,?/g, '');
    filtered = filtered.replace(/"run_id"\s*:\s*"[^"]*"\s*,?/g, '');
    filtered = filtered.replace(/"top"\s*,?/g, '');
    filtered = filtered.replace(/"num"\s*,?/g, '');
    filtered = filtered.replace(/",\s*"current_word_count"\s*:\s*\d+/g, '');
    filtered = filtered.replace(/",\s*"delta_html"\s*:\s*"/g, '');
    
    // STEP 5b: Remove progress/run_id fragments that leak mid-content (e.g. ,"totalWordsSoFar":269}}{"run_id":"run-...)
    filtered = filtered.replace(/,\s*"totalWordsSoFar"\s*:\s*\d+\s*}/g, '');
    filtered = filtered.replace(/\}\s*}\s*\{\s*"run_id"\s*:\s*"[^"]*"\s*(?:,\s*[^}]*)?\}/g, '');
    filtered = filtered.replace(/\}\s*\{\s*"run_id"\s*:\s*"[^"]*"\s*(?:,\s*[^}]*)?\}/g, '');
    filtered = filtered.replace(/\}\s*}\s*\{\s*"run_id"\s*:\s*"[^"]*"/g, '');
    filtered = filtered.replace(/\}\s*\{\s*"run_id"\s*:\s*"[^"]*"/g, '');
    
    // STEP 6: Final cleanup
    filtered = filtered.replace(/\\n/g, ' ');
    filtered = filtered.replace(/^\s*[{}\[\],:]+\s*/g, '');
    filtered = filtered.replace(/\s*[{}\[\],:]+\s*$/g, '');
    filtered = filtered.replace(/^[\s,]+/g, '');
    filtered = filtered.replace(/[\s,]+$/g, '');
    filtered = filtered.replace(/\s{2,}/g, ' ');
    
    return filtered.trim();
  };
  
  // Helper function to add debug log with rotation (keep only last 200)
  const addDebugLog = useCallback((log) => {
    setDebugLogs(prev => {
      const sanitizedLog = {
        ...log,
        details: sanitizeDebugDetails(log.details)
      };
      return [...prev, sanitizedLog].slice(-200); // Keep only last 200 entries
    });
  }, []);
  const [responseMeta, setResponseMeta] = useState({
    pageCount: 0,
    totalWords: 0,
    avgWordsPerPage: 0,
    totalLength: 0,
    run_id: null
  });
  const [responseStats, setResponseStats] = useState({
    pageCount: 0,
    totalWords: 0,
    avgWordsPerPage: 0
  });
  const [targetWordRange, setTargetWordRange] = useState(() => wordRangeFromCredits(resolveTaskCredits(task, module)));
  const [wordRangeDialogOpen, setWordRangeDialogOpen] = useState(false);
  const [wordRangeDialogMode, setWordRangeDialogMode] = useState('generate'); // generate | regenerate
  const [regenerateAcknowledged, setRegenerateAcknowledged] = useState(false);
  const [wordRangeDialogMin, setWordRangeDialogMin] = useState(() => String(wordRangeFromCredits(resolveTaskCredits(task, module)).min));
  const [wordRangeDialogMax, setWordRangeDialogMax] = useState(() => String(wordRangeFromCredits(resolveTaskCredits(task, module)).max));
  const [pendingPage, setPendingPage] = useState(null);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [planDocument, setPlanDocument] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [doneMissingEndSections, setDoneMissingEndSections] = useState(false);
  const planControllerRef = useRef(null);
  const buildControllerRef = useRef(null); // AbortController for build stream
  const hasCheckedForPagesRef = useRef(false);
  const checkingPagesRef = useRef(false);

  // Define refs early to break dependency chain and prevent webpack hang
  // These refs are used to access current values without creating dependencies
  const contextTreeRef = useRef(null);
  const qualificationRef = useRef(null);
  const styleProfileRef = useRef(null);
  const documentGuidelinesRef = useRef(null);
  const documentPlanRef = useRef(null);
  const planDocumentRef = useRef(null);
  const runIdRef = useRef(null);

  // Cleanup on unmount: abort all pending requests and clear state
  useEffect(() => {
    return () => {
      // Abort plan generation if active
      if (planControllerRef.current) {
        planControllerRef.current.abort();
        planControllerRef.current = null;
      }
      // Abort build generation if active
      if (buildControllerRef.current) {
        buildControllerRef.current.abort();
        buildControllerRef.current = null;
      }
      // Clear large string refs to free memory
      streamTextRef.current = '';
    };
  }, []);

  // Check for saved pages on mount or when qualification changes
  useEffect(() => {
    const loadSavedPagesOnMount = async () => {
      // Prevent multiple simultaneous requests
      if (checkingPagesRef.current || hasCheckedForPagesRef.current) {
        return;
      }
      
      // Only check once per qualification ID, and only if we don't have pages yet
      const qualId = qualification?.id;
      const currentPagesLength = pages.length;
      const currentBusy = busy;
      const currentPlanLoading = planLoading;
      if (qualId && currentPagesLength === 0 && !currentBusy && !currentPlanLoading) {
        checkingPagesRef.current = true;
        hasCheckedForPagesRef.current = true; // Mark as checked
        

        try {
          // Try by qualification ID
          const response = await fetch(`/api/learning/latest/pages?qualificationId=${qualId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.pages && data.pages.length > 0) {

              setRunId(data.runId);
              runIdRef.current = data.runId; // Update ref
              setPages(data.pages.map((p, idx) => ({
                page_number: p.page_number !== undefined && p.page_number !== null ? p.page_number : (idx + 1),
                title: p.title,
                html: p.html || p.html_content || '', // Support both html and html_content field names
                outline_path: p.outline_path,
                approximate_word_count: p.approximate_word_count,
                estimated_reading_time_min: p.estimated_reading_time_min
              })));
              setStatus('done');
              // Don't open modal automatically, just set the state so button appears
            } else {
              // No pages found - this is normal during generation or when starting fresh

            }
          } else if (response.status === 404) {
            // 404 is now handled by backend returning 200 with empty array, but handle it just in case

          }
        } catch (error) {
          // Don't log as error - 404s are expected when no pages exist yet
          if (error.message && !error.message.includes('404')) {

          }
        } finally {
          checkingPagesRef.current = false;
        }
      } else {
        checkingPagesRef.current = false;
      }
    };
    
    loadSavedPagesOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualification?.id]); // Only depend on qualification ID to prevent infinite loops - pages.length, busy, planLoading removed
  
  // Keep the word target aligned to this item's credits when the task changes.
  useEffect(() => {
    const next = wordRangeFromCredits(resolveTaskCredits(task, module));
    setTargetWordRange(next);
    targetWordRangeRef.current = next;
    setWordRangeDialogMin(String(next.min));
    setWordRangeDialogMax(String(next.max));
  }, [task?.code, task?.credits, module?.credits]);

  // Build context tree for this single task
  const contextTree = useMemo(() => {
    if (!task || !module || !qualification) return null;
    const contentSubtasks = filterChildrenForContentGeneration(task.children || []);
    const credits = resolveTaskCredits(task, module);
    const range = wordRangeFromCredits(credits);
    
    return {
      program: {
        name: qualification.name || qualification.title || 'Learning programme',
        ...(qualification.nqf_level != null && qualification.nqf_level !== ''
          ? { level: Number(qualification.nqf_level) }
          : {}),
        credits: qualification.total_credits || 120
      },
      item_scope: {
        task_code: task.code,
        task_title: task.title,
        task_description: task.description || '',
        task_type: task.type || (task.code?.startsWith('KT') ? 'KT' : task.code?.startsWith('PS') ? 'PS' : 'KT'),
        credits,
        word_range: range
      },
      modules: [{
        code: module.elo_number || module.code || `ELO${module.id}`,
        name: outcomeModuleDisplayName(module),
        description: module.description || '',
        credits: module.credits || 0,
        ...(task.nqf_level != null ? { nqf_level: task.nqf_level } : {}),
        tasks: [{
          code: task.code,
          name: task.title,
          description: task.description || '',
          type: task.type || (task.code?.startsWith('KT') ? 'KT' : task.code?.startsWith('PS') ? 'PS' : 'KT'),
          credits,
          ...(task.nqf_level != null ? { nqf_level: task.nqf_level } : {}),
          subtasks: contentSubtasks.map(child => ({
            code: child.code,
            title: child.title,
            description: child.description || '',
            node_type: child.node_type,
            assessment_cues: child.assessment_cues || [],
            ...(child.nqf_level != null ? { nqf_level: child.nqf_level } : {})
          }))
        }]
      }]
    };
  }, [task, module, qualification]);

  const styleProfile = useMemo(() => ({
    tone: 'professional yet accessible',
    readingLevel: 'upper-intermediate',
    regionFocus: 'South Africa',
    preferredWordsPerPage: '800-1000',
    citationStyle: 'Harvard',
    vectorStoreId: VECTOR_STORE_ID,
    vectorStoreGuideline: VECTOR_STORE_GUIDELINE,
    emphasizeLocalExamples: true
  }), [qualification?.nqf_level]);

  const documentGuidelines = useMemo(() => (
    `Use the vector store ${VECTOR_STORE_ID} writing rulebook for every paragraph: prioritise credible South African sources, provide inline Harvard citations, avoid Wikipedia, and keep the layout PDF-friendly with clear sections, figures, and tables.`
  ), []);

  const documentPlan = useMemo(() => {
    if (!contextTree || !Array.isArray(contextTree.modules)) {
      return null;
    }
    return {
      moduleCount: contextTree.modules.length,
      modules: contextTree.modules.map((mod, moduleIndex) => ({
        index: moduleIndex + 1,
        code: mod.code || mod.elo_number || `module-${moduleIndex + 1}`,
        title: mod.name || mod.title,
        taskCount: (mod.tasks || []).length,
        tasks: (mod.tasks || []).map((taskItem, taskIndex) => ({
          index: taskIndex + 1,
          code: taskItem.code,
          title: taskItem.name,
          subtaskCount: (taskItem.subtasks || []).length,
          subtasks: (taskItem.subtasks || []).map((subtask, subIndex) => ({
            index: subIndex + 1,
            code: subtask.code,
            title: subtask.title,
            node_type: subtask.node_type
          }))
        }))
      }))
    };
  }, [contextTree]);
  
  // Update refs when values change (these refs are defined early in the component)
  useEffect(() => {
    contextTreeRef.current = contextTree;
    qualificationRef.current = qualification;
    styleProfileRef.current = styleProfile;
    documentGuidelinesRef.current = documentGuidelines;
    documentPlanRef.current = documentPlan;
    planDocumentRef.current = planDocument;
    runIdRef.current = runId;
  }, [contextTree, qualification, styleProfile, documentGuidelines, documentPlan, planDocument, runId]);

  // Define startBuild first so it can be referenced by handleModalComplete
  const startBuild = useCallback(async (approvedPagesSnapshot, planDoc, optionalWordRange) => {
    // Use provided snapshot or empty array if not provided
    const currentPages = approvedPagesSnapshot !== undefined ? approvedPagesSnapshot : [];
    const rangeFromArg = optionalWordRange && typeof optionalWordRange.min === 'number' && typeof optionalWordRange.max === 'number' ? optionalWordRange : null;
    if (rangeFromArg) {
      targetWordRangeRef.current = rangeFromArg;
    }
    // Use optionalWordRange if provided, else state targetWordRange, else ref (so e.g. plan Approve uses current UI range)
    const rangeToSend = rangeFromArg ?? (targetWordRange && typeof targetWordRange.min === 'number' && typeof targetWordRange.max === 'number' ? targetWordRange : targetWordRangeRef.current);
    // Use refs to get current values without creating dependencies
    const currentContextTree = contextTreeRef.current;
    const currentQualification = qualificationRef.current;
    const currentStyleProfile = styleProfileRef.current;
    const currentDocumentGuidelines = documentGuidelinesRef.current;
    const currentDocumentPlan = documentPlanRef.current;
    const currentPlanDocument = planDocumentRef.current;
    const docPlan = planDoc !== undefined ? planDoc : currentPlanDocument;
    if (!currentContextTree || !currentContextTree.modules || currentContextTree.modules.length === 0) {
      setError('Unable to build context tree for this task.');
      return;
    }

    setBusy(true);
    setError(null);
    setDebugLogs([]);
    setStatus('generating');
    setModalOpen(true);
    wordCountReachedAtDoneRef.current = false;
    totalWordsAtDoneRef.current = 0;
    const sanitizedApprovedPages = Array.isArray(currentPages) ? currentPages : [];
    const continueFromPage = Math.max(1, sanitizedApprovedPages.length + 1);
    setPages(sanitizedApprovedPages);

    // CRITICAL: Determine if this is a continuation (has existing pages or content)
    // Use refs to access current values without dependencies
    const currentStreamText = streamTextRef.current;
    const currentRunIdFromRef = runIdRef.current;
    const isContinuation = sanitizedApprovedPages.length > 0 || (currentStreamText && currentStreamText.length > 0);
    
    // Generate run_id - use existing runId if continuing, otherwise create new
    const currentRunId = isContinuation && currentRunIdFromRef ? currentRunIdFromRef : `run-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // Update runId state immediately so it's available in all scopes
    if (currentRunId !== currentRunIdFromRef) {
    setRunId(currentRunId);
      runIdRef.current = currentRunId; // Update ref immediately
    }
    
    // CRITICAL: Don't reset streamText if continuing - preserve existing content
    // The backend will send existing pages' HTML in the first debug event
    if (!isContinuation) {
      streamTextRef.current = ''; // Reset ref
      // #region agent log
      if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:315',message:'setStreamText called from reset (new generation)',data:{source:'reset_new_generation'},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'F'})}).catch(()=>{});
      // #endregion
      setStreamText(''); // Only reset for new generation, not continuation
    }

      const payload = {
      run_id: currentRunId,
      context_tree: currentContextTree,
      target_audience: currentQualification?.name ? `Learners in ${currentQualification.name}` : 'Learners',
      qualification_code: currentQualification?.code || currentQualification?.id?.toString(),
      qualification_id: currentQualification?.id, // Add qualification_id for page saving
      target_page_count: null, // Word-based generation
      audienceProfile: {
        role: 'Learner',
        ...(currentQualification?.nqf_level != null && currentQualification?.nqf_level !== ''
          ? { level: `NQF Level ${currentQualification.nqf_level}` }
          : {})
      },
      stylePrefs: { 
        readingLevel: "Upper-Intermediate", 
        region: "ZA" 
      },
      styleProfile: currentStyleProfile,
      documentGuidelines: currentDocumentGuidelines,
        documentPlan: currentDocumentPlan,
        plan_document: docPlan,
      layoutPrefs: { 
        toc: true, 
        callouts: true 
      },
      citationStyle: "Harvard",
      approved_pages: sanitizedApprovedPages,
      mode: isContinuation ? 'continue' : 'new', // CRITICAL: Use 'continue' mode when continuing
      qualification_id: currentQualification?.id,
      qualification_name: currentQualification?.name,
      continue_from_page: continueFromPage,
      allowAutoContinue: true, // Enable auto-continuation to reach word count target
      enhancedMode: false,
      targetWordRange: { min: rangeToSend.min, max: rangeToSend.max }
    };

    // Create AbortController for cancellation
    const controller = new AbortController();
    buildControllerRef.current = controller;

    try {
      const response = await fetch('/api/learning/build-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal // Add abort signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finished = false;
      let loopCount = 0;

      while (!finished) {
        loopCount++;
        const { value, done } = await reader.read();
        // When stream ends, decode final chunk and process remaining buffer so we don't miss the 'done' event
        if (done) {
          buffer += decoder.decode(value != null ? value : new Uint8Array(0), { stream: false });
        } else {
          buffer += decoder.decode(value, { stream: true });
        }
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        if (done) {
          finished = true;
        }
        
        // #region agent log - Log each read iteration
        if (loopCount % 100 === 0 || loopCount <= 10) {
          if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:385',message:'SSE read iteration',data:{loopCount,valueLength:value?.length||0,partsCount:parts.length,bufferLength:buffer.length},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'H6'})}).catch(()=>{});
        }
        // #endregion

        for (const chunk of parts) {
          if (!chunk.trim()) continue;
          try {
          const lines = chunk.split('\n').filter(Boolean);
          const evtLine = lines.find(l => l.startsWith('event: '));
          const dataLine = lines.find(l => l.startsWith('data: '));
          if (!evtLine || !dataLine) continue;
          const event = evtLine.replace('event: ', '').trim();
          let data;
          try {
            data = JSON.parse(dataLine.replace('data: ', ''));
          } catch (parseError) {

            continue;
          }

            if (!data || typeof data !== 'object') {

              continue;
            }

            // #region agent log - Log every SSE event received
            if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:408',message:'SSE event received',data:{event,hasData:!!data,dataKeys:Object.keys(data||{}).slice(0,10)},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'H1'})}).catch(()=>{});
            // #endregion

            try {
          switch (event) {
            case 'status':
              setStatus('generating');
              addDebugLog({
                level: 'info',
                message: data.message || 'Status update',
                timestamp: data.timestamp,
                details: { message: data.message }
              });
              // Use ref to check responseJson without dependency
              if (!responseJson) {
                setResponseJson(data);
              }
              break;

            case 'content_delta':
              {
                // Handle content delta streaming
                // CRITICAL: Backend now sends full accumulatedHtml in content_delta events
                // Use that instead of appending deltas to prevent content loss
                const deltaData = data;
                const deltaHtml = deltaData.delta_html || '';
                const currentWordCount = deltaData.current_word_count || deltaData.total_words_so_far || 0;
                
                // CRITICAL: Prefer full accumulatedHtml from backend (contains all deltas)
                // This ensures we always have the complete document, not just incremental deltas
                const fullAccumulatedHtml = deltaData.accumulatedHtml || deltaData.fullText || null;
                
                // #region agent log
                if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:422',message:'content_delta event received',data:{hasAccumulatedHtml:!!deltaData.accumulatedHtml,hasFullText:!!deltaData.fullText,accumulatedHtmlLength:deltaData.accumulatedHtml?.length||0,fullTextLength:deltaData.fullText?.length||0,deltaHtmlLength:deltaHtml.length},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'C'})}).catch(()=>{});
                // #endregion
                
                // Reduced logging - only log significant deltas
                const prevLength = streamTextRef.current.length;
                
                if (fullAccumulatedHtml && fullAccumulatedHtml.length > 0) {
                  // Use the full accumulated HTML from backend (complete document)
                  const newLength = fullAccumulatedHtml.length;
                  const lengthDelta = newLength - prevLength;
                  
                  // #region agent log
                  if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:438',message:'Updating streamText from content_delta',data:{prevLength,newLength,lengthDelta},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'C'})}).catch(()=>{});
                  // #endregion
                  
                  // Backend sends authoritative full accumulated HTML each time. A "20% longer" rule
                  // skips legitimate updates: References, Glossary, and Abbreviations are large in absolute
                  // terms but small vs an already-long document. Apply any strictly longer snapshot.
                  const isFirstUpdate = prevLength === 0;
                  const meetsFirstUpdateThreshold = isFirstUpdate && newLength >= 100;
                  const meetsSubsequentUpdateThreshold = !isFirstUpdate && newLength > prevLength;
                  
                  if (meetsFirstUpdateThreshold || meetsSubsequentUpdateThreshold) {
                  // CRITICAL: Always update, even if length is the same (content might have changed)
                  // Use functional update to force React to detect the change
                    // CRITICAL: Filter out internal tool call data (query objects, URL fragments, etc.)
                    const filteredHtml = filterToolCallData(fullAccumulatedHtml);
                    streamTextRef.current = filteredHtml;
                  
                  // CRITICAL: Always call setStreamText, even if content appears the same
                  // The backend sends full accumulated HTML, so we must trust it and update
                  // Use a new string reference to force React to detect the change
                    const contentToSet = String(filteredHtml); // Create new string reference
                  
                  setStreamText(prev => {
                    // Force update by always returning the new content
                    // This ensures React detects the change even if strings are similar
                    const prevLength = prev.length;
                    const newLength = contentToSet.length;
                      
                      // #region agent log
                      if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:474',message:'setStreamText called from content_delta handler',data:{prevLength,newLength,delta:newLength-prevLength,source:'content_delta'},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'F'})}).catch(()=>{});
                      // #endregion
                    
                    // Reduced logging - only log significant updates (every 1000 chars or major deltas)
                    if (Math.abs(newLength - prevLength) > 500 || newLength % 1000 < 100) {
                      console.log('[TaskAgentBuilder] ✅ StreamText updated:', {
                        prevLength,
                        newLength,
                        delta: newLength - prevLength
                      });
                    }
                    // Always return new content, even if it appears the same
                    return contentToSet;
                  });
                  } else if (newLength < prevLength) {
                    // Received shorter content - preserve current longer content
                    console.warn('[TaskAgentBuilder] ⚠️ Received shorter fullAccumulatedHtml from content_delta, preserving current content:', {
                      receivedLength: newLength,
                      currentLength: prevLength,
                      difference: prevLength - newLength
                    });
                  }
                } else if (deltaHtml) {
                  // Fallback: If no accumulatedHtml, append delta (shouldn't happen but safe fallback)
                  // Apply same threshold check to prevent flickering
                  const isFirstUpdate = prevLength === 0;
                  const meetsFirstUpdateThreshold = isFirstUpdate && deltaHtml.length >= 100;
                  const minGrowth = Math.max(100, prevLength * 0.1);
                  const isSignificantlyLonger = deltaHtml.length >= minGrowth;
                  const meetsSubsequentUpdateThreshold = !isFirstUpdate && isSignificantlyLonger;
                  
                  if (meetsFirstUpdateThreshold || meetsSubsequentUpdateThreshold) {
                  console.log('[TaskAgentBuilder] ⚠️ No accumulatedHtml, falling back to delta append:', {
                    deltaHtmlLength: deltaHtml.length,
                    prevLength
                  });
                    // CRITICAL: Filter out internal tool call data before appending
                    const filteredDelta = filterToolCallData(deltaHtml);
                    if (filteredDelta.length > 0) {
                  setStreamText(prev => {
                        const newText = prev + filteredDelta;
                    streamTextRef.current = newText; // Keep ref in sync
                    console.log('[TaskAgentBuilder] ✅ Appended delta to streamText:', {
                      prevLength: prev.length,
                      newLength: newText.length,
                          delta: filteredDelta.length
                    });
                    return newText;
                  });
                    }
                  } else {
                    console.log('[TaskAgentBuilder] ⚠️ Skipping small delta in fallback handler:', {
                      deltaHtmlLength: deltaHtml.length,
                      prevLength,
                      meetsFirstUpdateThreshold,
                      meetsSubsequentUpdateThreshold
                    });
                  }
                } else {
                  console.warn('[TaskAgentBuilder] ⚠️ Content delta received but no accumulatedHtml or deltaHtml:', {
                    deltaDataKeys: Object.keys(deltaData),
                    hasAccumulatedHtml: !!deltaData.accumulatedHtml,
                    hasFullText: !!deltaData.fullText,
                    hasDeltaHtml: !!deltaHtml
                  });
                }
                
                // Always update word count for live display: use backend value or compute from content
                const wordCountToSet = currentWordCount > 0
                  ? currentWordCount
                  : (() => {
                      const html = fullAccumulatedHtml || streamTextRef.current || '';
                      if (!html) return 0;
                      const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                      return text ? text.split(/\s+/).filter(Boolean).length : 0;
                    })();
                setResponseMeta(prev => ({
                  ...prev,
                  totalWords: wordCountToSet
                }));
                
                addDebugLog({
                  level: 'info',
                  message: `Content delta received (${deltaHtml.length} chars, ${currentWordCount} words)`,
                  timestamp: new Date().toISOString()
                });
              }
              break;

            case 'page':

              
              // Extract and store progress snapshot if present (for info popup)
              if (data.progressStateSnapshot) {
                setProgressSnapshots(prev => {
                  const newSnapshots = [...prev, {
                    pageNumber: data.page_number,
                    timestamp: new Date().toISOString(),
                    snapshot: data.progressStateSnapshot
                  }];
                  // Keep only the last 5 snapshots to reduce memory usage
                  return newSnapshots.slice(-5);
                });
              }
              
              // Auto-add pages without approval - single run for 20-25 pages
              setPages(prevPages => {
                const currentPageCount = prevPages.length;
                
                // Ensure page_number is properly set and HTML content is preserved
                const incomingPageNumber = data.page_number;
                const calculatedPageNumber = currentPageCount + 1;
                const finalPageNumber = (incomingPageNumber !== undefined && incomingPageNumber !== null && !isNaN(incomingPageNumber) && incomingPageNumber > 0) 
                  ? incomingPageNumber 
                  : calculatedPageNumber;
                
                // Clean HTML to remove any progress snapshot data
                let cleanedHtml = data.html || data.html_content || '';
                if (cleanedHtml) {
                  // Remove JSON-like progress snapshot objects
                  cleanedHtml = cleanedHtml.replace(/<script[^>]*>[\s\S]*?progressStateSnapshot[\s\S]*?<\/script>/gi, '');
                  cleanedHtml = cleanedHtml.replace(/<pre[^>]*>[\s\S]*?progressStateSnapshot[\s\S]*?<\/pre>/gi, '');
                  cleanedHtml = cleanedHtml.replace(/<code[^>]*>[\s\S]*?progressStateSnapshot[\s\S]*?<\/code>/gi, '');
                  cleanedHtml = cleanedHtml.replace(/\{[^{}]*"totalWordsSoFar"[^{}]*\}/g, '');
                  cleanedHtml = cleanedHtml.replace(/\{[^{}]*"progressStateSnapshot"[^{}]*\}/g, '');
                }
                
                const pageData = {
                  ...data,
                  page_number: finalPageNumber,
                  html: cleanedHtml,
                  html_content: cleanedHtml
                };
                
                // Add page directly to array (no approval needed)
                const updatedPages = [...prevPages, pageData];
                
                addDebugLog({
                  level: 'info',
                  message: `Page ${finalPageNumber} generated: ${pageData.title || 'Untitled'}`,
                  timestamp: new Date().toISOString(),
                  details: {
                    page_number: finalPageNumber,
                    title: pageData.title,
                    word_count: pageData.approximate_word_count,
                    totalPages: updatedPages.length
                  }
                });
                
                return updatedPages;
              });
              break;

            case 'debug':
              {
                const details = data.details || {};
                // Use runId from ref (currentRunId is in startBuild scope)
                const activeRunId = runIdRef.current || 'unknown';
                
                // #region agent log - Check if top-level accumulatedHtml exists (should not, but log to debug)
                if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:600',message:'debug event structure check',data:{hasTopLevelAccumulatedHtml:!!data.accumulatedHtml,topLevelLength:data.accumulatedHtml?.length||0,hasDetailsAccumulatedHtml:!!details.accumulatedHtml,detailsLength:details.accumulatedHtml?.length||0,hasTopLevelFullText:!!data.fullText,topLevelFullTextLength:data.fullText?.length||0,hasDetailsFullText:!!details.fullText,detailsFullTextLength:details.fullText?.length||0},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'C'})}).catch(()=>{});
                // #endregion
                
                // Check for warning about incomplete generation/auto-continuation
                if (data.level === 'warning' && details.willContinue) {
                  // Calculate shortfall properly if not provided
                  const calculatedShortfall = details.shortfall !== undefined && details.shortfall !== null
                    ? details.shortfall
                    : (details.targetWordRangeMin && details.finalWordCount)
                      ? details.targetWordRangeMin - details.finalWordCount
                      : null;
                  
                  const errorDetails = {
                    finalWordCount: details.finalWordCount,
                    targetWordRange: `${details.targetWordRangeMin}-${details.targetWordRangeMax}`,
                    shortfall: calculatedShortfall,
                    hasReferences: details.hasReferences,
                    reason: details.reason,
                    runId: activeRunId,
                    timestamp: data.timestamp || new Date().toISOString()
                  };
                  

                  
                  // Show alert to user about auto-continuation with option to cancel
                  const userWantsToCancel = !window.confirm(
                    `⚠️ Generation Incomplete - Auto-Continuation Detected\n\n` +
                    `Generated: ${details.finalWordCount?.toLocaleString() || 'N/A'} words\n` +
                    `Target: ${details.targetWordRangeMin?.toLocaleString() || 'N/A'}-${details.targetWordRangeMax?.toLocaleString() || 'N/A'} words\n` +
                    `Shortfall: ${calculatedShortfall?.toLocaleString() || 'N/A'} words\n` +
                    `Has References: ${details.hasReferences ? 'Yes' : 'No'}\n` +
                    `Reason: ${details.reason || 'Unknown'}\n\n` +
                    `Run ID: ${activeRunId}\n\n` +
                    `The backend is automatically continuing generation.\n` +
                    `This will incur additional costs.\n\n` +
                    `Click OK to allow continuation, or Cancel to stop generation.`
                  );
                  
                  if (userWantsToCancel) {
                    // User wants to cancel - abort the stream

                    if (buildControllerRef.current) {
                      buildControllerRef.current.abort();
                      buildControllerRef.current = null;
                    }
                    setStatus('cancelled');
                    setBusy(false);
                    setError(`Generation cancelled by user during auto-continuation. Word count: ${details.finalWordCount?.toLocaleString() || 'N/A'}/${details.targetWordRangeMin?.toLocaleString() || 'N/A'}`);
                    addDebugLog({
                      level: 'warning',
                      message: `User cancelled auto-continuation`,
                      timestamp: new Date().toISOString(),
                      details: { ...errorDetails, userCancelled: true }
                    });
                    // Throw error to break out of the stream processing loop
                    throw new Error('User cancelled auto-continuation');
                  }
                  
                  // User confirmed - but since allowAutoContinue is false, backend won't continue anyway
                  // Log the confirmation but note that continuation is disabled
                  addDebugLog({
                    level: 'warning',
                    message: `⚠️ User confirmed continuation but auto-continuation is disabled. Manual continuation required.`,
                    timestamp: data.timestamp || new Date().toISOString(),
                    details: { ...errorDetails, userConfirmed: true, autoContinueDisabled: true }
                  });
                  
                  // Show alert that manual continuation is needed
                  alert(`Note: Auto-continuation is disabled.\n\n` +
                        `The backend will NOT automatically continue.\n` +
                        `To continue generation, please use the "Continue Generation" button in the modal.`);
                } else if (details.allowAutoContinue === false || !details.willContinue) {
                  // Auto-continuation is disabled - backend won't continue
                  // Calculate shortfall properly if not provided
                  const calculatedShortfall = details.shortfall !== undefined && details.shortfall !== null
                    ? details.shortfall
                    : (details.targetWordRangeMin && details.finalWordCount)
                      ? details.targetWordRangeMin - details.finalWordCount
                      : null;
                  
                  const targetMin = details.targetWordRangeMin ?? targetWordRangeRef.current?.min ?? 'N/A';
                  const targetMax = details.targetWordRangeMax ?? targetWordRangeRef.current?.max ?? 'N/A';
                  const finalCount = details.finalWordCount ?? responseMetaRef.current?.totalWords ?? 'N/A';
                  const errorDetailsForLog = {
                    finalWordCount: details.finalWordCount,
                    targetWordRange: `${targetMin}-${targetMax}`,
                    shortfall: calculatedShortfall,
                    hasReferences: details.hasReferences,
                    reason: details.reason,
                    runId: activeRunId,
                    timestamp: data.timestamp || new Date().toISOString()
                  };

                  addDebugLog({
                    level: 'info',
                    message: `Auto-continuation disabled - generation will stop at ${finalCount}/${targetMin} words`,
                    timestamp: data.timestamp || new Date().toISOString(),
                    details: errorDetailsForLog
                  });
                }
                
                if (details.stats) {
                  setResponseStats(details.stats);
                  setResponseMeta(prev => ({
                    ...prev,
                    pageCount: details.stats.pageCount || prev.pageCount,
                    totalWords: details.stats.totalWords || prev.totalWords,
                    avgWordsPerPage: details.stats.avgWordsPerPage || prev.avgWordsPerPage,
                    run_id: prev.run_id || activeRunId
                  }));
                }
                if (details.json) {
                  setResponseJson(details.json);
                }
                // Extract stream text deltas from debug events
                // Prefer accumulatedHtml if available (clean HTML), otherwise use fullText
                // fullText might contain JSON query/search data mixed with HTML, so we need to extract HTML
                let htmlContent = null;
                
                // #region agent log
                if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:696',message:'debug event received',data:{hasAccumulatedHtml:!!details.accumulatedHtml,hasFullText:!!details.fullText,accumulatedHtmlLength:details.accumulatedHtml?.length||0,fullTextLength:details.fullText?.length||0},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'C'})}).catch(()=>{});
                // #endregion
                
                // First, try to use accumulatedHtml if available (cleanest source)
                // CRITICAL: Always prefer accumulatedHtml from backend - it contains ALL deltas
                // CRITICAL: Never use shorter content - always preserve the longest accumulated content
                // Check both top-level and details for accumulatedHtml/fullText (backend sends in both places)
                const receivedAccumulatedHtml = data.accumulatedHtml || details.accumulatedHtml;
                const receivedFullText = data.fullText || details.fullText;
                const currentStreamTextLength = streamTextRef.current.length;
                
                if (receivedAccumulatedHtml !== undefined && receivedAccumulatedHtml !== null && receivedAccumulatedHtml.length > 0) {
                  // Always set htmlContent if we have receivedAccumulatedHtml
                  // The threshold check will be done in the functional update below
                  htmlContent = receivedAccumulatedHtml;
                } else if (receivedFullText !== undefined && receivedFullText !== null) {
                  // Extract HTML from fullText (might contain JSON query/search data)
                  let fullText = details.fullText;
                  
                  // Remove JSON artifacts that might leak into HTML content
                  // Remove query/search JSON objects: {"query":"...","numResults":...}
                  // Remove URL JSON objects: {"url":"..."}
                  // Remove run_id and progress state JSON: {"run_id":"...","totalWordsSoFar":...}
                  fullText = fullText
                    // Remove query/search JSON objects (complete objects)
                    .replace(/\{"query"\s*:\s*"[^"]*"\s*,\s*"numResults"\s*:\s*\d+[^}]*\}/g, '')
                    .replace(/\{"query"\s*:\s*"[^"]*"\s*,\s*"run_id"\s*:\s*"[^"]*"[^}]*\}/g, '')
                    .replace(/\{"query"\s*:\s*"[^"]*"\s*,\s*"topKChunks"\s*:\s*\d+[^}]*\}/g, '')
                    // Remove URL JSON objects (with or without quotes around URL)
                    .replace(/\{"url"\s*:\s*"[^"]*"\}/g, '')
                    .replace(/\{"url"\s*:\s*[^}]*\}/g, '')
                    // Remove run_id and progress state JSON objects
                    .replace(/\{"run_id"\s*:\s*"[^"]*"\s*,\s*"totalWordsSoFar"\s*:\s*\d+\}/g, '')
                    .replace(/\{"run_id"\s*:\s*"[^"]*"\}/g, '')
                    // Remove progressStateSnapshot patterns (including nested objects)
                    // Match "progressStateSnapshot":{...} with nested braces
                    .replace(/"progressStateSnapshot"\s*:\s*\{[^}]{0,2000}?\}/g, '')
                    // Match incomplete progressStateSnapshot (cut off mid-object)
                    .replace(/"progressStateSnapshot"\s*:\s*\{[^<]*/g, '')
                    .replace(/",\s*"current_word_count"\s*:\s*\d+\s*,\s*"progressStateSnapshot"\s*:\s*\{[^}]*\}/g, '')
                    // Remove current_word_count patterns (various formats)
                    .replace(/",\s*"current_word_count"\s*:\s*\d+/g, '')
                    .replace(/"\s*,\s*"current_word_count"\s*:\s*\d+/g, '')
                    // Match incomplete patterns like ","current_word_count": (cut off)
                    .replace(/",\s*"current_word_count"\s*:\s*/g, '')
                    .replace(/"\s*,\s*"current_word_count"\s*:\s*/g, '')
                    .replace(/",\s*"delta_html"\s*:\s*"/g, '')
                    .replace(/",\s*"run_id"\s*:\s*"[^"]*"/g, '')
                    .replace(/\{"run_id"\s*:\s*"[^"]*",\s*"delta_html"\s*:\s*"/g, '')
                    .replace(/"\s*,\s*"progressStateSnapshot"\s*:\s*\{[^}]*\}/g, '')
                    .replace(/\{"docId"[^}]*\}/g, '')
                    .replace(/\{"targetWordRange"\s*:\s*\{[^}]*\}[^}]*\}/g, '')
                    // Remove any remaining JSON-like patterns with run_id
                    .replace(/\{[^}]*"run_id"[^}]*\}/g, '')
                    // Remove any remaining JSON-like patterns with query
                    .replace(/\{[^}]*"query"[^}]*\}/g, '')
                    // Remove literal \n strings
                    .replace(/\\n/g, ' ');
                  
                  // Check if fullText contains HTML (starts with < or contains HTML tags)
                  const htmlMatch = fullText.match(/<[^>]+>/);
                  let extractedHtml = null;
                  if (htmlMatch) {
                    // Find the start of HTML content (first < tag)
                    const htmlStartIndex = fullText.indexOf(htmlMatch[0]);
                    if (htmlStartIndex >= 0) {
                      // Extract everything from the first HTML tag onwards
                      extractedHtml = fullText.substring(htmlStartIndex);
                    } else {
                      extractedHtml = fullText; // Fallback to full text if no HTML found
                    }
                  } else {
                    // No HTML tags found, might be plain text or JSON only
                    // Check if it looks like JSON (starts with { or [)
                    if (fullText.trim().startsWith('{') || fullText.trim().startsWith('[')) {
                      // This is likely JSON only, skip it
                      extractedHtml = null;
                    } else {
                      extractedHtml = fullText; // Use as-is if it's plain text
                    }
                  }
                  
                  // Always set htmlContent if we have extracted HTML
                  // The threshold check will be done in the functional update below
                  if (extractedHtml && extractedHtml.length > 0) {
                    htmlContent = extractedHtml;
                  }
                }
                
                // Clean JSON artifacts from htmlContent if it was set
                // Use the centralized filterToolCallData function that handles:
                // - Complete JSON objects with query, url, numResults, etc.
                // - Partial/incremental JSON being built character by character
                // - Tool call metadata fragments
                if (htmlContent) {
                  // #region agent log
                  const beforeFilterLength = htmlContent.length;
                  const beforeFilterPreview = htmlContent.substring(0, 200);
                  if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:971',message:'Before filterToolCallData',data:{beforeLength:beforeFilterLength,beforePreview:beforeFilterPreview},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'J1'})}).catch(()=>{});
                  // #endregion
                  htmlContent = filterToolCallData(htmlContent);
                  // #region agent log
                  const afterFilterLength = htmlContent.length;
                  const afterFilterPreview = htmlContent.substring(0, 200);
                  if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:973',message:'After filterToolCallData',data:{afterLength:afterFilterLength,afterPreview:afterFilterPreview,removed:beforeFilterLength-afterFilterLength},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'J1'})}).catch(()=>{});
                  // #endregion
                }
                
                // Update stream text if we have valid HTML content
                // CRITICAL: Only update if htmlContent is longer than or equal to current content
                // This prevents shorter content from overwriting longer accumulated content
                if (htmlContent && htmlContent.length > 0) {
                  const isInitialLoad = details.isInitialLoad === true;
                  // Capture htmlContent in a const to ensure it's available in the closure
                  const htmlContentToUse = htmlContent;
                  
                  // #region agent log
                  if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:888',message:'About to call setStreamText with functional update',data:{htmlContentLength:htmlContentToUse.length,isInitialLoad,hasHtmlContent:!!htmlContentToUse},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'C'})}).catch(()=>{});
                  // #endregion
                  
                  // Use functional update to get the latest streamText state value
                  // This ensures we're checking against the actual current state, not a stale ref
                  setStreamText(prev => {
                    const currentLength = prev.length;
                    
                    // #region agent log
                    if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:819',message:'Updating streamText from debug event',data:{isInitialLoad,htmlContentLength:htmlContentToUse.length,prevLength:currentLength,willUpdate:isInitialLoad||htmlContentToUse.length>=currentLength},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'C'})}).catch(()=>{});
                    // #endregion
                    
                    // Same rule as content_delta: accept any longer snapshot from backend (see content_delta handler).
                    const isFirstUpdate = currentLength === 0;
                    const meetsFirstUpdateThreshold = isFirstUpdate || isInitialLoad;
                    const meetsSubsequentUpdateThreshold = !isFirstUpdate && htmlContentToUse.length > currentLength;
                    
                    // #region agent log
                    if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:902',message:'Checking if should update streamText from debug event',data:{isInitialLoad,htmlContentLength:htmlContentToUse.length,prevLength:currentLength,willUpdate:isInitialLoad||meetsFirstUpdateThreshold||meetsSubsequentUpdateThreshold},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'C'})}).catch(()=>{});
                    // #endregion
                    
                    if (isInitialLoad || meetsFirstUpdateThreshold || meetsSubsequentUpdateThreshold) {
                      // Update ref to keep it in sync
                      streamTextRef.current = htmlContentToUse;
                      // Create a new string reference to force React to detect the change
                      const contentToSet = String(htmlContentToUse);
                      // #region agent log
                      if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:951',message:'setStreamText called from debug event handler',data:{htmlContentLength:htmlContentToUse.length,prevLength:currentLength,contentToSetLength:contentToSet.length,source:isInitialLoad?'debug_event_isInitialLoad':'debug_event_regular'},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'F'})}).catch(()=>{});
                      // #endregion
                      return contentToSet;
                  } else {
                      // Shorter or same-length snapshot — keep current editor content
                      // #region agent log
                      if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:920',message:'Skipping debug streamText update (not longer)',data:{htmlContentLength:htmlContentToUse.length,prevLength:currentLength},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'C'})}).catch(()=>{});
                      // #endregion
                      return prev; // Keep current state, don't update
                    }
                  });
                  // setStreamText callback handles the state update
                  // Don't use return here as it would exit the entire startBuild function!
                  // Just let the code continue to the end of the case block
                }
                
                // Fallback handlers below (for accumulatedText/preview) - only reached if htmlContent is empty
                if (false && htmlContent && htmlContent.length > 0) {
                  // This code path should not be reached - kept for reference
                  const isInitialLoad = details.isInitialLoad === true;
                  const currentLength = streamTextRef.current.length;
                  
                  // #region agent log
                  if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:819',message:'Updating streamText from debug event',data:{isInitialLoad,htmlContentLength:htmlContent.length,prevLength:currentLength,willUpdate:isInitialLoad||htmlContent.length>=currentLength},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'C'})}).catch(()=>{});
                  // #endregion
                  
                  // CRITICAL: Only update if new content is significantly longer to avoid flickering on small deltas
                  // Require at least 20% growth or 500 chars more (whichever is larger) to reduce update frequency
                  // This prevents displaying each threshold update separately - only show substantial accumulated updates
                  const minGrowth = Math.max(500, currentLength * 0.2);
                  const isSignificantlyLonger = htmlContent.length >= currentLength + minGrowth;
                  
                  // #region agent log
                  if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:902',message:'Checking if should update streamText from debug event',data:{isInitialLoad,htmlContentLength:htmlContent.length,prevLength:currentLength,minGrowth,isSignificantlyLonger,willUpdate:isInitialLoad||(htmlContent.length>=currentLength&&isSignificantlyLonger)},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'C'})}).catch(()=>{});
                  // #endregion
                  
                  // Only update if content is significantly longer
                  // For the first update (when currentLength === 0), require at least 500 chars to avoid displaying tiny initial deltas
                  // For subsequent updates, require at least 20% growth or 500 chars more
                  const isFirstUpdate = currentLength === 0;
                  const meetsFirstUpdateThreshold = isFirstUpdate && htmlContent.length >= 500;
                  const meetsSubsequentUpdateThreshold = !isFirstUpdate && htmlContent.length >= currentLength && isSignificantlyLonger;
                  
                  if (isInitialLoad || meetsFirstUpdateThreshold || meetsSubsequentUpdateThreshold) {
                  } else {
                    // Received shorter content - preserve current longer content
                    console.warn('[TaskAgentBuilder] ⚠️ Received shorter htmlContent from debug event, preserving current content:', {
                      receivedLength: htmlContent.length,
                      currentLength: currentLength,
                      difference: currentLength - htmlContent.length
                    });
                  }
                  
                  // ALWAYS update word count from live word count (calculated from deltas)
                  // Always update to ensure UI reflects current word count
                  if (details.liveWordCount !== undefined && details.liveWordCount !== null) {
                    setResponseMeta(prev => {
                      const newMeta = {
                        ...prev,
                        totalWords: details.liveWordCount
                      };
                      return newMeta;
                    });
                  }
                }
                
                // Fallback to accumulatedText or preview if no HTML content found
                // Apply same threshold check to prevent flickering
                if ((!htmlContent || htmlContent.length === 0) && (details.accumulatedText || details.preview)) {
                  const rawTextDelta = details.accumulatedText || details.preview || '';
                  // CRITICAL: Filter out tool call data before processing
                  const textDelta = filterToolCallData(rawTextDelta);
                  if (textDelta && textDelta.length > 0) {
                    const currentLength = streamTextRef.current.length;
                    const isFirstUpdate = currentLength === 0;
                    const newLength = textDelta.length;
                    const deltaLength = newLength - currentLength;
                    const meetsFirstUpdateThreshold = isFirstUpdate && newLength >= 500;
                    const meetsSubsequentUpdateThreshold = !isFirstUpdate && newLength > currentLength;
                    
                    if (meetsFirstUpdateThreshold || meetsSubsequentUpdateThreshold) {
                    setStreamText(prev => {
                      // Append new text that hasn't been added yet
                        const prevLength = prev.length;
                        if (newLength > prevLength) {
                        // Extract only the new portion and append
                          const newText = prev + textDelta.substring(prevLength);
                        streamTextRef.current = newText; // Update ref
                        return newText;
                      }
                      return prev;
                    });
                    } else {
                      console.log('[TaskAgentBuilder] ⚠️ Skipping small text delta in fallback handler:', {
                        textDeltaLength: textDelta.length,
                        currentLength,
                        newLength,
                        deltaLength,
                        meetsFirstUpdateThreshold,
                        meetsSubsequentUpdateThreshold
                      });
                    }
                  }
                }
                if (data.message && !data.message.includes('raw_model_stream_event')) {
                  addDebugLog({
                      level: data.level || 'info',
                      message: data.message || 'Debug info',
                      timestamp: data.timestamp || new Date().toISOString(),
                    details: sanitizeDebugDetails(details || data)
                  });
                }
              }
              break;

            case 'done':
              setStatus('done');
              setBusy(false);
              // Backend may send hasAllEndSections; also check content client-side so we always show banner if sections missing
              const contentToCheck = data.fullText || data.accumulatedHtml || streamTextRef.current || '';
              const hasRef = /<h[1-6][^>]*>[\s\S]*?references?[\s\S]*?<\/h[1-6]>/i.test(contentToCheck);
              const hasGloss = /<h[1-6][^>]*>[\s\S]*?glossary[\s\S]*?<\/h[1-6]>/i.test(contentToCheck);
              const hasAbbr = /<h[1-6][^>]*>[\s\S]*?abbreviations?[\s\S]*?<\/h[1-6]>/i.test(contentToCheck);
              const allEndSectionsPresent = hasRef && hasGloss && hasAbbr;
              setDoneMissingEndSections(data.incomplete === true || data.hasAllEndSections === false || !allEndSectionsPresent);
              
              // Get current runId from ref (currentRunId is in startBuild scope)
              const activeRunIdForDone = runIdRef.current || currentRunId || 'unknown';
              
              // CRITICAL: Check if word count target was reached - use RUN's target (ref), not state
              const currentResponseMeta = responseMetaRef.current;
              const finalWordCount = data.totalWords || currentResponseMeta?.totalWords || 0;
              const runTarget = targetWordRangeRef.current;
              const targetWordMin = (runTarget && typeof runTarget.min === 'number') ? runTarget.min : targetWordRange.min;
              const targetWordMax = (runTarget && typeof runTarget.max === 'number') ? runTarget.max : targetWordRange.max;
              const wordCountReached = finalWordCount >= targetWordMin;
              wordCountReachedAtDoneRef.current = wordCountReached;
              totalWordsAtDoneRef.current = finalWordCount;
              // If done event has no totalWords but we have substantial content, treat as complete so close doesn't show false "incomplete"
              if (!wordCountReached && (data.fullText || data.accumulatedHtml)?.length > 2000) {
                wordCountReachedAtDoneRef.current = true;
              }
              
              // CRITICAL: Use the final complete result from the agent for CKEditor
              // The backend sends fullText or accumulatedHtml in the done event, which contains
              // the complete, final result from the agent. This should be used to update CKEditor
              // instead of the incrementally built content, as it represents the agent's final output.
              let finalCompleteResult = data.fullText || data.accumulatedHtml || null;
              
              // Clean JSON artifacts and tool call data from final result
              // Use the centralized filterToolCallData function for consistency
              if (finalCompleteResult) {
                finalCompleteResult = filterToolCallData(finalCompleteResult);
              }
              
              if (finalCompleteResult && finalCompleteResult.trim().length > 0) {
                // Update streamText with the final complete result from the agent
                streamTextRef.current = finalCompleteResult;
                setStreamText(finalCompleteResult);
                
                addDebugLog({
                  level: 'info',
                  message: `Using final complete result from agent (${finalCompleteResult.length.toLocaleString()} chars)`,
                  timestamp: data.timestamp || new Date().toISOString(),
                  details: {
                    finalResultLength: finalCompleteResult.length,
                    source: 'done_event_final_result'
                  }
                });
              } else {
                // Fallback: Use ref to access streamText without dependency if no final result provided
              const currentStreamTextForDone = streamTextRef.current;
                
                addDebugLog({
                  level: 'warning',
                  message: 'No final complete result in done event, preserving incrementally built content',
                  timestamp: data.timestamp || new Date().toISOString(),
                  details: {
                    streamTextLength: currentStreamTextForDone?.length || 0,
                    hasFullText: !!data.fullText,
                    hasAccumulatedHtml: !!data.accumulatedHtml
                  }
                });
              }
              
              // Update responseMeta with word count only (no page references)
              // Use the word count from done event for metadata
              setResponseMeta(prev => ({
                ...prev,
                totalWords: data.totalWords || prev.totalWords,
                run_id: prev.run_id || activeRunIdForDone
              }));
              
              // When word count not reached: set error state and log only (no blocking alert)
              if (!wordCountReached) {
                const currentStreamTextLength = streamTextRef.current?.length || 0;
                addDebugLog({
                  level: 'warning',
                  message: `Word count below target: ${finalWordCount.toLocaleString()}/${targetWordMin.toLocaleString()} words`,
                  timestamp: new Date().toISOString(),
                  details: {
                    finalWordCount,
                    targetWordRange: `${targetWordMin}-${targetWordMax}`,
                    streamTextLength: currentStreamTextLength,
                    runId: activeRunIdForDone
                  }
                });
                setError(`Word count: ${finalWordCount.toLocaleString()}/${targetWordMin.toLocaleString()} words. You can close and keep the content or continue later.`);
              } else {
                // Get current streamText length from ref
                const currentStreamTextLength = streamTextRef.current?.length || 0;
                addDebugLog({
                level: 'info',
                  message: `Generation complete: ${finalWordCount.toLocaleString()} words`,
                timestamp: data.timestamp || new Date().toISOString(),
                  details: {
                    totalWords: finalWordCount,
                    streamTextLength: currentStreamTextLength,
                    targetReached: true
                  }
                });
              }
              break;

            case 'error':
              // Check if error is a termination error (client disconnect) - handle gracefully
              const isTerminationError = data.message?.includes('terminated') || 
                                         data.message?.includes('ECONNRESET') ||
                                         data.message?.includes('EPIPE');
              
              if (isTerminationError) {
                // Termination is expected when client disconnects - don't show as error
                // Just mark as cancelled and preserve content
                setStatus('cancelled');
                setBusy(false);
                addDebugLog({
                  level: 'info',
                  message: 'Stream terminated (client disconnected)',
                  timestamp: data.timestamp || new Date().toISOString()
                });
              } else {
                // Real error - show it
                setStatus('error');
                setBusy(false);
                setError(data.message || 'Generation failed');
                addDebugLog({
                  level: 'error',
                  message: data.message || 'Error occurred',
                  timestamp: data.timestamp || new Date().toISOString(),
                  details: sanitizeDebugDetails(data)
                });
              }
              break;

            default:
              break;
            }
            } catch (eventError) {
              // #region agent log
              if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:1234',message:'Event processing error (eventError)',data:{error:eventError?.message||String(eventError),stack:eventError?.stack?.substring(0,500)},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'H7'})}).catch(()=>{});
              // #endregion
              // Continue processing other events
            }
          } catch (chunkError) {
            // #region agent log
            if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:1237',message:'Chunk processing error (chunkError)',data:{error:chunkError?.message||String(chunkError),stack:chunkError?.stack?.substring(0,500)},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'H7'})}).catch(()=>{});
            // #endregion
            // Continue processing other chunks
          }
        }
        // #region agent log - Log after processing all chunks in this read iteration
        if (DEBUG_INGEST_URL) fetch(DEBUG_INGEST_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskAgentBuilder.jsx:1240',message:'Finished processing chunks, about to read next SSE data',data:{loopCount,partsProcessed:parts.length},timestamp:Date.now(),sessionId:'debug-session',runId:runIdRef.current,hypothesisId:'H6'})}).catch(()=>{});
        // #endregion
      }
      // If stream ended normally but no 'done' event was processed, still mark complete so Save shows and Cancel hides
      if (streamTextRef.current && streamTextRef.current.length > 0) {
        setStatus('done');
        setBusy(false);
      }
    } catch (e) {
      // Handle termination errors gracefully
      const isTerminationError = e.name === 'AbortError' || 
                                  e.message?.includes('terminated') ||
                                  e.message?.includes('ECONNRESET') ||
                                  e.message?.includes('EPIPE') ||
                                  e.message === 'User cancelled auto-continuation';
      
      if (isTerminationError) {
        setStatus('cancelled');
        setBusy(false);
        // Don't show error for termination - it's expected when client disconnects
        addDebugLog({
          level: 'info',
          message: e.message || 'Build cancelled by user',
          timestamp: new Date().toISOString()
        });
      } else {
        setStatus('error');
        setBusy(false);
        setError(e.message || 'Build failed');
        addDebugLog({
          level: 'error',
          message: `Build error: ${e.message}`,
          timestamp: new Date().toISOString(),
          details: { error: e.message, stack: e.stack ? e.stack.substring(0, 200) : undefined }
        });
      }
    } finally {
      // Clear controller ref and clean up
      if (buildControllerRef.current === controller) {
        buildControllerRef.current = null;
      }
      // Clear any pending state that might hold memory
      // Note: Don't clear streamText here as it should persist for viewing
    }
  }, [targetWordRange]); // Include targetWordRange so payload uses current UI range when optionalWordRange not passed

  const handleModalComplete = useCallback((completedPages) => {
    // Use authoritative word count from 'done' event when available (avoids false alert when pages have stale/missing approximate_word_count)
    if (wordCountReachedAtDoneRef.current) {
      setError(null);
      setPages(completedPages || []);
      setModalOpen(false);
      setBusy(false);
      if (onComplete) {
        onComplete(completedPages, task.code);
      }
      return;
    }
    // Word-based generation: modal passes empty pages on close; don't show "incomplete" when we have no page data
    if (!completedPages || completedPages.length === 0) {
      setPages(completedPages || []);
      setModalOpen(false);
      setBusy(false);
      if (onComplete) {
        onComplete(completedPages || [], task.code);
      }
      return;
    }
    // Page-based fallback: derive from pages (may be stale)
    const completedWords = completedPages.reduce((sum, p) => sum + (p.approximate_word_count || 0), 0);
    const targetWordMin = targetWordRange.min;
    const wordCountReached = completedWords >= targetWordMin;
    
    // CRITICAL: Do NOT automatically restart if word count not reached
    // Show alert and require user confirmation to prevent unexpected costs
    if (!wordCountReached) {
      const shortfall = targetWordMin - completedWords;
      const errorDetails = {
        completedWords,
        targetWordRange: `${targetWordMin}-${targetWordRange.max}`,
        shortfall,
        pagesCount: completedPages.length,
        runId: runId,
        timestamp: new Date().toISOString(),
        error: 'Word count target not reached - manual continuation required'
      };
      

      
      // Show alert with debug information and require user confirmation
      const userConfirmed = window.confirm(
        `⚠️ Generation Incomplete - Word Count Not Reached\n\n` +
        `Generated: ${completedWords.toLocaleString()} words\n` +
        `Target: ${targetWordMin.toLocaleString()}-${targetWordRange.max.toLocaleString()} words\n` +
        `Shortfall: ${shortfall.toLocaleString()} words\n` +
        `Pages: ${completedPages.length}\n\n` +
        `Run ID: ${runId || 'N/A'}\n\n` +
        `Generation will NOT automatically restart to prevent unexpected costs.\n\n` +
        `Click OK to manually continue generation, or Cancel to keep current content.`
      );
      
      if (!userConfirmed) {
        // User cancelled - just update pages and close, don't restart

        setPages(completedPages || []);
        setModalOpen(false);
        setBusy(false);
        setError(`Word count not reached: ${completedWords.toLocaleString()}/${targetWordMin.toLocaleString()} words (user cancelled continuation)`);
        if (onComplete) {
          onComplete(completedPages, task.code);
        }
        return;
      }
      
      // User confirmed - log and continue

      addDebugLog({
        level: 'warning',
        message: `Manual continuation requested: ${completedWords.toLocaleString()}/${targetWordMin.toLocaleString()} words`,
        timestamp: new Date().toISOString(),
        details: errorDetails
      });
      
      // CRITICAL: Don't reset streamText when continuing - preserve existing content
      // The backend will send existing pages' HTML in the first debug event
      setModalOpen(false);
      // Use setTimeout to ensure modal closes before starting new generation
      setTimeout(() => {
        startBuild(completedPages);
      }, 100);
      return;
    }
    
    // Otherwise, just update pages and close
    setPages(completedPages || []);
    setModalOpen(false);
    // Reset busy state when generation is complete
    setBusy(false);
    if (onComplete) {
      onComplete(completedPages, task.code);
    }
  }, [onComplete, task?.code, startBuild, runId, targetWordRange]);

  const handleSaved = useCallback((...args) => {
    setError(null);
    if (onSaved) onSaved(...args);
  }, [onSaved]);

  const handlePlanModalClose = useCallback(() => {
    if (planControllerRef.current) {
      planControllerRef.current.abort();
      planControllerRef.current = null;
    }
    setPlanModalOpen(false);
  }, []);

  const fetchPlan = useCallback(async () => {
    // Use refs to get current values without creating dependencies
    const currentContextTree = contextTreeRef.current;
    const currentQualification = qualificationRef.current;
    const currentStyleProfile = styleProfileRef.current;
    const currentDocumentGuidelines = documentGuidelinesRef.current;
    const currentDocumentPlan = documentPlanRef.current;
    
    if (!currentContextTree || !currentContextTree.modules || currentContextTree.modules.length === 0) {
      setPlanError('Unable to build context tree for this task.');
      setPlanLoading(false);
      return;
    }

    if (planControllerRef.current) {
      planControllerRef.current.abort();
    }
    const controller = new AbortController();
    planControllerRef.current = controller;

    const payload = {
      context_tree: currentContextTree,
      target_audience: currentQualification?.name ? `Learners in ${currentQualification.name}` : 'Learners',
      qualification_code: currentQualification?.code || currentQualification?.id?.toString(),
      qualification_id: currentQualification?.id,
      target_page_count: null, // Word-based generation (plan mode may still use page count)
      audienceProfile: {
        role: 'Learner',
        ...(currentQualification?.nqf_level != null && currentQualification?.nqf_level !== ''
          ? { level: `NQF Level ${currentQualification.nqf_level}` }
          : {})
      },
      stylePrefs: {
        readingLevel: "Upper-Intermediate",
        region: "ZA"
      },
      styleProfile: currentStyleProfile,
      documentGuidelines: currentDocumentGuidelines,
      documentPlan: currentDocumentPlan,
      layoutPrefs: {
        toc: true,
        callouts: true
      },
      citationStyle: "Harvard"
    };

    try {
      const res = await fetch('/api/learning/plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || res.statusText || 'Plan generation failed');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finished = false;

      while (!finished) {
        const { value, done } = await reader.read();
        if (done) {
          finished = true;
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const chunk of parts) {
          if (!chunk.trim()) continue;
          const lines = chunk.split('\n').filter(Boolean);
          const evtLine = lines.find(l => l.startsWith('event: '));
          const dataLine = lines.find(l => l.startsWith('data: '));
          if (!evtLine || !dataLine) continue;
          const event = evtLine.replace('event: ', '').trim();
          let data;
          try {
            data = JSON.parse(dataLine.replace('data: ', ''));
          } catch (parseError) {

            continue;
          }


          switch (event) {
            case 'plan':

              setPlanDocument(data.planDocument || null);
              setPlanError(null);
              setPlanLoading(false);
              break;
            case 'status':
            case 'debug':
              // Log status/debug events but don't process them
              break;
            case 'error':

              setPlanError(data.message || 'Plan generation failed');
              finished = true;
              break;
            case 'done':

              finished = true;
              break;
            default:

              break;
          }
        }
      }
    } catch (planError) {
      if (planError.name !== 'AbortError') {
        setPlanError(planError.message || 'Plan generation failed');
      }
    } finally {
      setPlanLoading(false);
      if (planControllerRef.current === controller) {
        planControllerRef.current = null;
      }
    }
  }, []); // Empty dependency array - using refs to access current values, breaking dependency chain

  // Check for saved pages when opening modal
  const checkForSavedPages = useCallback(async (runIdToCheck, qualId) => {
    try {
      // Try by runId first if provided
      if (runIdToCheck && runIdToCheck.startsWith('run-')) {
        const response = await fetch(`/api/learning/${runIdToCheck}/pages`);
        if (response.ok) {
          const data = await response.json();
          if (data.pages && data.pages.length > 0) {

            return data;
          }
        }
      }
      
      // Fallback: try by qualification ID
      if (qualId) {
        const response = await fetch(`/api/learning/latest/pages?qualificationId=${qualId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.pages && data.pages.length > 0) {

            return data;
          } else {
            // No pages found - this is normal

          }
        } else if (response.status === 404) {
          // 404 is now handled by backend returning 200 with empty array, but handle it just in case

        }
      }
    } catch (error) {

    }
    return null;
  }, []);

  const handleGenerateClick = useCallback(async () => {
    const currentRunId = runIdRef.current;
    const currentQualification = qualificationRef.current;
    const hasExistingForThisTask = pages.length > 0 || showViewSavedButton;

    if (hasExistingForThisTask) {
      if (onViewSavedContent && task?.code) {
        onViewSavedContent(task.code, task.title);
      }
      return;
    }

    const savedPages = await checkForSavedPages(currentRunId, currentQualification?.id);
    
    if (savedPages && savedPages.pages.length > 0 && currentRunId && savedPages.runId === currentRunId) {
      setRunId(savedPages.runId);
      runIdRef.current = savedPages.runId;
      setPages(savedPages.pages.map((p, idx) => ({
        page_number: p.page_number !== undefined && p.page_number !== null ? p.page_number : (idx + 1),
        title: p.title,
        html: p.html || p.html_content || '',
        outline_path: p.outline_path,
        approximate_word_count: p.approximate_word_count,
        estimated_reading_time_min: p.estimated_reading_time_min
      })));
      setStatus('done');
      setBusy(false);
      setModalOpen(true);
      const totalWords = savedPages.pages.reduce((sum, p) => sum + (p.approximate_word_count || 0), 0);
      const totalReadingTime = savedPages.pages.reduce((sum, p) => sum + (parseFloat(p.estimated_reading_time_min) || 0), 0);
      setResponseStats({
        pageCount: savedPages.pages.length,
        totalWords,
        avgWordsPerPage: savedPages.pages.length ? Math.round((totalWords / savedPages.pages.length) * 10) / 10 : 0,
        estimatedReadingTimeMin: Math.round(totalReadingTime * 10) / 10,
        avgReadingTimePerPage: savedPages.pages.length ? Math.round((totalReadingTime / savedPages.pages.length) * 10) / 10 : 0
      });
      return;
    }
    
    regenerateFromScratchRef.current = false;
    setRegenerateAcknowledged(false);
    setWordRangeDialogMode('generate');
    setWordRangeDialogMin(String(targetWordRange.min));
    setWordRangeDialogMax(String(targetWordRange.max));
    setWordRangeDialogOpen(true);
  }, [checkForSavedPages, targetWordRange, pages.length, showViewSavedButton, onViewSavedContent, task]);

  const doRegenerateFromScratch = useCallback(async (range) => {
    const currentQualification = qualificationRef.current;
    const code = task?.code;
    if (!currentQualification?.id) {
      alert('Qualification ID is required to regenerate');
      return;
    }
    if (!code) {
      alert('Task code is required to regenerate');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) { headers['Authorization'] = `Bearer ${token}`; }
      const response = await fetch(
        `/api/qualifications/${currentQualification.id}/learning-content/saved-document/${encodeURIComponent(code)}?confirm=regenerate_from_scratch`,
        { method: 'DELETE', headers }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to clear saved document: ${response.statusText}`);
      }
      setPages([]);
      setRunId(null);
      runIdRef.current = null;
      setPlanDocument(null);
      setStatus(null);
      setError(null);
      setResponseHtml('');
      setResponseJson(null);
      streamTextRef.current = '';
      setStreamText('');
      setPendingPage(null);
      setResponseMeta(prev => ({ ...prev, pageCount: 0, totalWords: 0, avgWordsPerPage: 0, totalLength: 0, run_id: null }));
      setResponseStats({ pageCount: 0, totalWords: 0, avgWordsPerPage: 0, estimatedReadingTimeMin: 0, avgReadingTimePerPage: 0 });
      setDebugLogs([]);
      setModalOpen(true);
      setBusy(true);
      setStatus('generating');
      if (onCleared) onCleared(code);
      startBuild([], null, range);
    } catch (error) {
      setBusy(false);
      setStatus(null);
      alert(`Failed to regenerate from scratch: ${error.message}`);
    }
  }, [startBuild, task?.code, onCleared]);

  const closeWordRangeDialog = useCallback(() => {
    regenerateFromScratchRef.current = false;
    setRegenerateAcknowledged(false);
    setWordRangeDialogOpen(false);
  }, []);

  const handleWordRangeDialogStart = useCallback(async () => {
    const min = parseInt(wordRangeDialogMin, 10);
    const max = parseInt(wordRangeDialogMax, 10);
    if (Number.isNaN(min) || Number.isNaN(max) || min < 1 || max < 1 || min > max) {
      setError('Please enter valid min and max word counts (min ≤ max).');
      return;
    }
    const range = { min, max };
    setTargetWordRange(range);
    const isFromScratch = regenerateFromScratchRef.current || wordRangeDialogMode === 'regenerate';
    if (isFromScratch) {
      if (!regenerateAcknowledged) {
        setError('Confirm that you want to delete this task\'s document and generate from scratch.');
        return;
      }
      setWordRangeDialogOpen(false);
      setRegenerateAcknowledged(false);
      setError(null);
      await doRegenerateFromScratch(range);
      return;
    }
    if (showViewSavedButton || pages.length > 0) {
      setWordRangeDialogOpen(false);
      return;
    }
    setWordRangeDialogOpen(false);
    setError(null);
    startBuild([], null, range);
  }, [wordRangeDialogMin, wordRangeDialogMax, startBuild, showViewSavedButton, pages.length, wordRangeDialogMode, regenerateAcknowledged, doRegenerateFromScratch]);

  const handleRegenerateFromScratch = useCallback(() => {
    regenerateFromScratchRef.current = true;
    setRegenerateAcknowledged(false);
    setWordRangeDialogMode('regenerate');
    setWordRangeDialogMin(String(targetWordRange.min));
    setWordRangeDialogMax(String(targetWordRange.max));
    setWordRangeDialogOpen(true);
  }, [targetWordRange]);

  // Approval system removed - content deltas are automatically streamed without approval

  const hasContent = pages.length > 0 || planDocument !== null;
  const hasExistingContent = hasContent || showViewSavedButton;
  const isRegenerateDialog = wordRangeDialogMode === 'regenerate';
  
  // Debug: log when button should appear
  // Removed console.log useEffect

  return (
    <>
      <Dialog open={wordRangeDialogOpen} onClose={closeWordRangeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{isRegenerateDialog ? 'Generate from scratch' : 'Generate content'}</DialogTitle>
        <DialogContent>
          {isRegenerateDialog ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This starts a new document for <strong>{task?.code || 'this task'}</strong> only. Other tasks are not changed.
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {isRegenerateDialog
              ? 'Workflow: you confirm below, the current saved document for this task is deleted, then a new document is generated from scratch. This never runs automatically.'
              : 'The range below is sized to this item\'s credits. The AI will write only about this knowledge task or practical skill.'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {task?.code || 'This item'} · {taskCredits} credits · target {creditBasedRange.min.toLocaleString()}–{creditBasedRange.max.toLocaleString()} words
            {' '}(~{creditBasedPages.min}–{creditBasedPages.max} PDF pages). Generation will continue until the minimum is reached and the text matches this item.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <TextField
              label="Minimum words"
              type="number"
              inputProps={{ min: 1, max: 1000000 }}
              value={wordRangeDialogMin}
              onChange={(e) => setWordRangeDialogMin(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Maximum words"
              type="number"
              inputProps={{ min: 1, max: 1000000 }}
              value={wordRangeDialogMax}
              onChange={(e) => setWordRangeDialogMax(e.target.value)}
              fullWidth
              size="small"
            />
            {isRegenerateDialog && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={regenerateAcknowledged}
                    onChange={(e) => setRegenerateAcknowledged(e.target.checked)}
                    color="error"
                  />
                }
                label="I understand this deletes the current document for this task and generates a new one from scratch."
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeWordRangeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={isRegenerateDialog ? 'error' : 'primary'}
            onClick={handleWordRangeDialogStart}
            disabled={isRegenerateDialog && !regenerateAcknowledged}
          >
            {isRegenerateDialog ? 'Delete and generate from scratch' : 'Start generation'}
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {showViewSavedButton && onViewSavedContent && (
          <Tooltip title="Open saved document">
            <span>
              <IconButton
                color="success"
                size="small"
                onClick={() => onViewSavedContent(task.code, task.title)}
                aria-label="Open saved document"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={busy ? 'Generating…' : hasExistingContent ? 'Document already generated. Use Generate from scratch to replace it.' : 'Generate content'}>
          <span>
            <IconButton
              color="primary"
              size="small"
              onClick={handleGenerateClick}
              disabled={busy || planLoading || !contextTree || hasExistingContent}
              aria-label={hasExistingContent ? 'Document already generated' : 'Generate'}
            >
              {busy ? <CircularProgress size={18} /> : <AutoAwesomeIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {hasExistingContent && (
          <Tooltip title={busy ? 'Generating…' : 'Generate from scratch. Deletes this task\'s saved document, then creates a new one.'}>
            <span>
              <IconButton
                color="warning"
                size="small"
                onClick={handleRegenerateFromScratch}
                disabled={busy || planLoading || !contextTree}
                aria-label="Generate from scratch"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <LearningProgressModal
        open={modalOpen}
        taskCode={task?.code}
        onClose={() => {
          // Reset busy state when modal closes (only if generation is not active)
          // If status is 'done', 'cancelled', or 'error', reset busy state
          if (status === 'done' || status === 'cancelled' || status === 'error' || status !== 'generating') {
            setBusy(false);
          }
          setDoneMissingEndSections(false);
          // Allow closing at any time - user should be able to cancel
          setModalOpen(false);
        }}
        onCancel={() => {
          // Cancel the build stream - stop generation but preserve content
          if (buildControllerRef.current) {

            buildControllerRef.current.abort();
            buildControllerRef.current = null;
          }
          // Set status to cancelled but DON'T clear streamText or close modal
          // User can view generated content and close via X button
          setStatus('cancelled');
          setBusy(false);
          addDebugLog({
            level: 'info',
            message: 'Generation cancelled by user - content preserved',
            timestamp: new Date().toISOString()
          });
        }}
        runId={runId}
        pages={pages}
        debugLogs={debugLogs}
        status={status}
        error={error}
        responseHtml={responseHtml}
        responseMeta={responseMeta}
        responseJson={responseJson}
        targetWordRange={targetWordRange}
        streamText={streamText}
        progressSnapshots={progressSnapshots}
        onComplete={handleModalComplete}
        onSaved={handleSaved}
        qualification={qualification}
        module={module}
        doneMissingEndSections={doneMissingEndSections}
        onEndSectionsGenerated={() => setDoneMissingEndSections(false)}
      />
      <DocumentPlanModal
        open={planModalOpen}
        loading={planLoading}
        planDocument={planDocument}
        error={planError}
        onClose={handlePlanModalClose}
        onApprove={() => {
          handlePlanModalClose();
          startBuild([], planDocument);
        }}
      />
      {/* PageApprovalModal removed - no longer needed for word-count-based streaming */}
    </>
  );
}

